<!--
  The dispatch sheet (Truck Delivery Order) — phase 2, surface 10.

  Printed from the dispatch board (LoadList.vue) and the load detail
  (LoadsView.vue), always the same way: the page constructs this component
  with `renderSheet(loadsPrint, { load })`, mounts it off-document, and hands
  `el` and `[cssText]` to the shared printer
  (`$printer.print(content.el, [content.cssText])`, lib/printer.js). That
  contract is unchanged by this pass: props in, `el` + `cssText` out.

  What changed is the sheet itself. The old markup was a float-based layout
  under a `.dispatch-print` class whose CSS existed in THREE places at once —
  this file's cssText, subhaulerPrint.vue's cssText, and the live document's
  ui/src/sass/_print.scss — with `<h3>`/`<h4>` tags used as a font-size scale
  rather than structure. It now renders through the one print visual system
  (lib/printSheet.js, `.lw-sheet__*`): letterhead with the org's legal name
  and the document's name in the same place every sheet puts them, tracked-
  caps labels, one table style, black on white throughout. All of the sheet's
  DATA — bill-to block, order/PO/customer-ref numbers, truck/trailer/driver
  assignment, the stop list with its midnight-means-date-only rule, notes —
  is carried over field for field.

  The print CSS stays self-contained in this component's cssText (base + the
  sheet's own column widths); nothing here reads a screen token, because the
  printed iframe has no app stylesheet to resolve one against.
-->
<template>
  <div class="lw-sheet">
    <header class="lw-sheet__letterhead">
      <p class="lw-sheet__org">
        {{ orgLegalName }}
      </p>
      <div class="lw-sheet__doc">
        <p class="lw-sheet__doc-title">Truck Delivery Order</p>
        <p class="lw-sheet__doc-date">
          {{ convertTime(load.created_at) }}
        </p>
      </div>
    </header>

    <div class="lw-sheet__section lw-sheet__meta">
      <div class="lw-sheet__meta-col">
        <div class="lw-sheet__meta-item">
          <span class="lw-sheet__label">Bill to</span>
          <p>
            <span>{{ load.customer.name }}</span>
            <br />
            <span v-html="address(load.customer.address)" />
            <br />
            {{ load.customer.phone }}
          </p>
        </div>
      </div>
      <div class="lw-sheet__meta-col lw-sheet__meta-col--end">
        <div class="lw-sheet__meta-item">
          <span class="lw-sheet__label">Ord #</span>
          <p>{{ load.id }}-{{ load.edit_number || 0 }}</p>
        </div>
        <div class="lw-sheet__meta-item">
          <span class="lw-sheet__label">P.O. #</span>
          <p>{{ load.po }}</p>
        </div>
        <div class="lw-sheet__meta-item">
          <span class="lw-sheet__label">Cust ref #</span>
          <p>{{ load.customer_reference }}</p>
        </div>
      </div>
    </div>

    <div class="lw-sheet__section lw-sheet__meta-row">
      <div class="lw-sheet__meta-item">
        <span class="lw-sheet__label">Truck</span>
        <p>{{ (load.truck && load.truck.unit_number) || '—' }}</p>
      </div>
      <div class="lw-sheet__meta-item">
        <span class="lw-sheet__label">Trailer</span>
        <p>{{ (load.trailer && load.trailer.unit_number) || '—' }}</p>
      </div>
      <div class="lw-sheet__meta-item">
        <span class="lw-sheet__label">Driver</span>
        <p>{{ (load.driver && load.driver.name) || '—' }}</p>
      </div>
    </div>

    <div class="lw-sheet__section">
      <table class="lw-sheet__table">
        <thead>
          <tr>
            <th class="lw-sheet-loads__col-type">Pickup/Drop</th>
            <th>Location</th>
            <th class="lw-sheet-loads__col-time">Date/Time</th>
          </tr>
        </thead>
        <tbody>
          <!--
            Keyed by index, which is what the keyless `v-for` this replaces
            already did: the sheet is rendered once off-document by
            `renderSheet` and handed straight to the printer, so there is no
            reorder for a stable key to survive, and a stop on an unsaved
            load has no id to key on.
          -->
          <tr v-for="(stop, index) in load.stops" :key="index">
            <td>{{ uppercase(stop.type) }}</td>
            <td>
              <p>
                <span>{{ stop.location.name }}</span>
                <br />
                <span v-html="address(stop.location.address)" />
              </p>
            </td>
            <td v-if="isMidnight(stop.time)">
              {{ convertTimeJustDate(stop.time) }}
            </td>
            <td v-else>
              {{ convertTime(stop.time) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="lw-sheet__section">
      <span class="lw-sheet__label">Notes</span>
      <p class="lw-sheet__note">
        {{ load.notes }}
      </p>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone';
import { orgZone } from '../../../lib/orgTime.js';
import { escapeHtmlWithBreaks } from '../../../lib/escapeHtml';
import { SHEET_BASE_CSS } from '../../../lib/printSheet';
export default {
  props: {
    load: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      // The whole sheet system plus this sheet's own column widths. Self-
      // contained on purpose: the printer hands the iframe exactly this
      // string and nothing else.
      cssText:
        SHEET_BASE_CSS +
        `
          .lw-sheet-loads__col-type { width: 18%; }
          .lw-sheet-loads__col-time { width: 26%; }
        `,
    };
  },
  computed: {
    // W0-4: carrier identity comes from organizations, with the legacy
    // hardcoded name as fallback until the org has loaded.
    orgLegalName() {
      const store = require('../../../store').default;
      const org = store.state.organization;
      return (org && org.legal_name) || 'Ladeway.';
    },
    // The zone this sheet's times are printed in: the tenant's `org.timezone`
    // (lib/orgTime.js), read from the same store module the letterhead above
    // reads, and for the same reason — the printer hands this component to a
    // detached iframe, so it has no injected $store to reach through.
    orgTz() {
      return orgZone(require('../../../store').default);
    },
  },
  methods: {
    uppercase(value) {
      return value.toUpperCase();
    },
    address(str) {
      // SEC-N: escape HTML before converting newlines (rendered via v-html).
      return escapeHtmlWithBreaks(str);
    },
    isMidnight(time) {
      // In the SAME zone the sheet renders in — now the tenant's, via orgTz
      // above. The old check used the machine's local time while the cells
      // below printed a fixed zone, so on any machine in a different zone (CI
      // runs UTC) a stop that renders as "12:00 am" was not detected as
      // midnight and the date-only rule misfired. Reading both from one
      // computed is what keeps them from drifting apart again.
      return moment(time).tz(this.orgTz).format('HH:mm') === '00:00';
    },
    convertTime(e) {
      return moment(e).tz(this.orgTz).format('M/D/Y [at] h:mm a');
    },
    convertTimeJustDate(e) {
      return moment(e).tz(this.orgTz).format('M/D/Y');
    },
  },
};
</script>
